import { ProductTradeInProductImagesData } from '@features/product-page/api/trade-in/getProductImages';

import type { MappedOption, Variance } from './types';

export function filterOptions(options?: MappedOption[], selectedBrand?: number) {
  if (!options) return;
  if (!selectedBrand) return options;

  return options.filter(value => {
    if (typeof selectedBrand === 'undefined') return false;

    return Array.isArray(value)
      ? value.some(option => option.brandId === selectedBrand)
      : value.brandId === selectedBrand;
  });
}

export function filterOptionsByBrand(options?: MappedOption[], brandId?: number) {
  return options?.filter(option => option.brandId === brandId);
}

export function filterVariantOptions(options?: MappedOption[], selectedOption?: string | null) {
  if (!options) return;

  const variantsByOptionId = mapOptionsToVariants(options, 0);

  return selectedOption && variantsByOptionId?.[selectedOption]
    ? options?.filter(option =>
        Object.values(variantsByOptionId[selectedOption])
          .map(variant => variant.optionId)
          .includes(option.optionId)
      )
    : undefined;
}

export function mapOptionsToVariants(options: MappedOption[], level: number) {
  const variations: Record<string, Array<Variance & { optionId: number }>> = {};

  const sortOrdersAtLevel = options
    .map(option => option.variances[level]?.sortOrder)
    .filter((s): s is number => s !== undefined);

  const targetSortOrder = sortOrdersAtLevel.length > 0 ? Math.min(...sortOrdersAtLevel) : undefined;

  options?.forEach(option => {
    const variance = option.variances[level];
    const value = variance?.value;

    if (variance && variance.sortOrder === targetSortOrder) {
      if (variations[value]) {
        variations[value].push({ ...variance, optionId: option.optionId });
      } else {
        variations[value] = [{ ...variance, optionId: option.optionId }];
      }
    }
  });

  return variations;
}

export function getImageId({
  variance,
  options,
  productImages
}: {
  variance: Variance & { optionId: number };
  options?: MappedOption[];
  productImages?: ProductTradeInProductImagesData | null;
}) {
  const productId =
    variance?.showImage &&
    productImages &&
    options?.find(option => option.optionId === variance.optionId)?.productIdForImage;

  return productId && productImages?.[`product_${productId}`]?.primaryImage.id;
}

export function getSelectedVariantImageId(
  options: MappedOption[] | undefined,
  variantChoices: string[] | undefined,
  productImages: ProductTradeInProductImagesData | null | undefined
): number | false | undefined {
  const selectedVariant = options?.find(option =>
    option.variances.every((variance, index) => variance.value === variantChoices?.[index])
  );
  return productImages?.[`product_${selectedVariant?.productIdForImage}`]?.primaryImage.id;
}
